import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiSave,
  FiX,
  FiCalendar,
} from "react-icons/fi";

import {
  getExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
} from "./CashAndDisbursementsService";

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);

  const [totalExpensesAmount, setTotalExpensesAmount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [editingExpense, setEditingExpense] = useState(null);

  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "",
    amount: "",
    expenseType: "",
    paymentMethod: "",
    expenseDate: new Date().toISOString().split("T")[0],
    notes: "",
  });

  /* =========================================================
     LOAD EXPENSES
  ========================================================= */

  const loadExpenses = async () => {
    if (!selectedDate) return;

    try {
      setLoading(true);

      const response = await getExpenses(selectedDate);

      const result = response?.data || response;

      const expensesData = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.expenses)
        ? result.expenses
        : [];

      setExpenses(expensesData);

      const totalAmount = expensesData.reduce(
        (sum, item) =>
          sum + Number(item?.amount || 0),
        0
      );

      setTotalExpensesAmount(totalAmount);

      setTotalCount(expensesData.length);
    } catch (error) {
      console.error(
        "GET EXPENSES ERROR:",
        error
      );

      setExpenses([]);
      setTotalExpensesAmount(0);
      setTotalCount(0);

      Swal.fire({
        icon: "error",
        title: "خطأ",
        text:
          error?.response?.data?.message ||
          "حدث خطأ أثناء جلب المصروفات",
        confirmButtonText: "حسناً",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenses();
  }, [selectedDate]);

  /* =========================================================
     FORM HELPERS
  ========================================================= */

  const resetForm = () => {
    setForm({
      title: "",
      amount: "",
      expenseType: "",
      paymentMethod: "",
      expenseDate: selectedDate,
      notes: "",
    });

    setEditingExpense(null);
  };

  const openCreateModal = () => {
    resetForm();

    setIsModalOpen(true);
  };

  const openEditModal = (expense) => {
    setEditingExpense(expense);

    setForm({
      title: expense?.title || "",
      amount: expense?.amount ?? "",
      expenseType: expense?.expenseType || "",
      paymentMethod: expense?.paymentMethod || "",
      expenseDate: expense?.expenseDate
        ? String(expense.expenseDate).split("T")[0]
        : selectedDate,
      notes: expense?.notes || "",
    });

    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;

    setIsModalOpen(false);

    resetForm();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =========================================================
     SAVE EXPENSE
  ========================================================= */

  const handleSubmit = async (event) => {
  event.preventDefault();

  if (!form.title.trim()) {
    Swal.fire({
      icon: "warning",
      title: "تنبيه",
      text: "برجاء إدخال بيان المصروف",
      confirmButtonText: "حسناً",
    });
    return;
  }

  if (form.amount === "" || Number(form.amount) <= 0) {
    Swal.fire({
      icon: "warning",
      title: "تنبيه",
      text: "برجاء إدخال قيمة صحيحة للمصروف",
      confirmButtonText: "حسناً",
    });
    return;
  }

  if (!form.paymentMethod) {
    Swal.fire({
      icon: "warning",
      title: "تنبيه",
      text: "برجاء اختيار طريقة الدفع",
      confirmButtonText: "حسناً",
    });
    return;
  }

  if (!form.expenseDate) {
    Swal.fire({
      icon: "warning",
      title: "تنبيه",
      text: "برجاء اختيار تاريخ المصروف",
      confirmButtonText: "حسناً",
    });
    return;
  }

  const payload = {
    title: form.title.trim(),
    amount: Number(form.amount),
    paymentMethod: form.paymentMethod,
    expenseType: form.expenseType.trim(),
    expenseDate: `${form.expenseDate}T00:00:00`,
    notes: form.notes.trim(),
  };

  try {
    setSaving(true);

    let response;

    if (editingExpense) {
      response = await updateExpense(
        editingExpense.id,
        payload
      );
    } else {
      response = await createExpense(payload);
    }

    if (
      response?.statusCode &&
      response.statusCode >= 400
    ) {
      throw new Error(
        response.message || "حدث خطأ أثناء حفظ المصروف"
      );
    }

    // اقفل المودال وأوقف التحميل فور نجاح الطلب
    setSaving(false);
    setIsModalOpen(false);
    resetForm();


    await loadExpenses();

    await Swal.fire({
      icon: "success",
      title: editingExpense
        ? "تم تعديل المصروف"
        : "تم إضافة المصروف",
      text: editingExpense
        ? "تم تعديل بيانات المصروف بنجاح"
        : "تم إضافة المصروف بنجاح",
      confirmButtonText: "حسناً",
    });

  } catch (error) {
    console.error("SAVE EXPENSE ERROR:", error);

    setSaving(false);

    Swal.fire({
      icon: "error",
      title: "خطأ",
      text:
        error?.response?.data?.message ||
        error?.message ||
        "حدث خطأ أثناء حفظ المصروف",
      confirmButtonText: "حسناً",
    });
  }
};

  /* =========================================================
     DELETE EXPENSE
  ========================================================= */

  const handleDelete = async (expense) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "حذف المصروف",
      text: `هل أنت متأكد من حذف "${expense?.title || ""}"؟`,
      showCancelButton: true,
      confirmButtonText: "نعم، حذف",
      cancelButtonText: "إلغاء",
      confirmButtonColor: "#d33",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);

      await deleteExpense(expense.id);

      await Swal.fire({
        icon: "success",
        title: "تم الحذف",
        text: "تم حذف المصروف بنجاح",
        confirmButtonText: "حسناً",
      });

      await loadExpenses();
    } catch (error) {
      console.error(
        "DELETE EXPENSE ERROR:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "خطأ",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "حدث خطأ أثناء حذف المصروف",
        confirmButtonText: "حسناً",
      });
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     FORMATTERS
  ========================================================= */

  const formatNumber = (value) =>
    Number(value || 0).toLocaleString("ar-EG");

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString(
      "ar-EG"
    );
  };

  const formattedTotal = useMemo(
    () => formatNumber(totalExpensesAmount),
    [totalExpensesAmount]
  );

  return (
    <>
      <section className="cash-card expenses-card">
        {/* =====================================================
            HEADER
        ===================================================== */}

        <div className="cash-card-header">
          <div>
            <h2>حركة المنصرف</h2>

            <p>
              إدارة ومتابعة المصروفات
            </p>
          </div>

          <button
            type="button"
            className="cash-primary-button"
            onClick={openCreateModal}
          >
            <FiPlus size={16} />

            إضافة مصروف
          </button>
        </div>

        {/* =====================================================
            SUMMARY
        ===================================================== */}

        <div className="cash-summary">
          <div className="cash-summary-item">
            <span>
              إجمالي المنصرف
            </span>

            <strong>
              {loading
                ? "..."
                : formattedTotal}
            </strong>
          </div>

          <div className="cash-summary-item">
            <span>
              عدد المصروفات
            </span>

            <strong>
              {loading
                ? "..."
                : formatNumber(totalCount)}
            </strong>
          </div>
        </div>

        {/* =====================================================
            DATE FILTER
        ===================================================== */}

        <div className="expenses-date-filter">
          <div className="expenses-date-title">
            <FiCalendar size={15} />

            <span>
              حركة المنصرف اليومية
            </span>
          </div>

          <div className="expenses-date-controls">
            <label htmlFor="expenses-date">
              التاريخ
            </label>

            <input
              id="expenses-date"
              type="date"
              value={selectedDate}
              onChange={(event) =>
                setSelectedDate(
                  event.target.value
                )
              }
            />

            <button
              type="button"
              className="cash-refresh-button"
              onClick={loadExpenses}
              disabled={loading}
            >
              <FiRefreshCw
                size={15}
                className={
                  loading
                    ? "cash-spin"
                    : ""
                }
              />

              تحديث
            </button>
          </div>
        </div>

        {/* =====================================================
            TABLE
        ===================================================== */}

        <div className="cash-table-wrapper">
          <table className="cash-table">
            <thead>
              <tr>
                <th>م</th>
                <th>البيان</th>
                <th>القيمة</th>
                <th>النوع</th>
                <th>التاريخ</th>
                <th>الملاحظات</th>
                <th>الإجراءات</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="6"
                    className="cash-table-empty"
                  >
                    جاري تحميل المصروفات...
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="cash-table-empty"
                  >
                    لا توجد مصروفات في هذا اليوم
                  </td>
                </tr>
              ) : (
                expenses.map((expense, index) => (
                  <tr
                    key={
                      expense?.id ??
                      index
                    }
                  >
                    <td>
                      {index + 1}
                    </td>

                    <td>
                      {expense?.title || "-"}
                    </td>

                    <td className="cash-money">
                      {formatNumber(
                        expense?.amount
                      )}
                    </td>
                    <td className="cash-money">
                      { expense?.paymentMethod }
                    </td>

                    <td>
                      {formatDate(
                        expense?.expenseDate
                      )}
                    </td>

                    <td>
                      {expense?.notes || "-"}
                    </td>

                    <td>
                      <div className="cash-table-actions">
                        <button
                          type="button"
                          className="cash-icon-button cash-edit-button"
                          title="تعديل"
                          onClick={() =>
                            openEditModal(expense)
                          }
                        >
                          <FiEdit2 size={15} />
                        </button>

                        <button
                          type="button"
                          className="cash-icon-button cash-delete-button"
                          title="حذف"
                          onClick={() =>
                            handleDelete(expense)
                          }
                        >
                          <FiTrash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {expenses.length > 0 && !loading && (
              <tfoot>
                <tr>
                  <td
                    colSpan="2"
                    className="cash-total-label"
                  >
                    إجمالي المنصرف
                  </td>

                  <td className="cash-money cash-total-value">
                    {formatNumber(
                      totalExpensesAmount
                    )}
                  </td>

                  <td colSpan="3">
                    -
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      {/* =======================================================
          MODAL
      ======================================================= */}

      {isModalOpen && (
        <div className="cash-modal-overlay">
          <div className="cash-modal">
            <div className="cash-modal-header">
              <div>
                <h3>
                  {editingExpense
                    ? "تعديل المصروف"
                    : "إضافة مصروف"}
                </h3>

                <p>
                  أدخل بيانات المصروف
                </p>
              </div>

              <button
                type="button"
                className="cash-modal-close"
                onClick={closeModal}
                disabled={saving}
              >
                <FiX size={18} />
              </button>
            </div>

            <form
              className="cash-expense-form"
              onSubmit={handleSubmit}
            >
              <div className="cash-form-group">
                <label>
                  البيان
                  <span>*</span>
                </label>

                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="مثال: شراء مستلزمات"
                  disabled={saving}
                />
              </div>

              <div className="cash-form-row">
                <div className="cash-form-group">
                  <label>
                    القيمة
                    <span>*</span>
                  </label>

                  <input
                    type="number"
                    name="amount"
                    value={form.amount}
                    onChange={handleChange}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    disabled={saving}
                  />
                </div>

             
              </div>

                <div className="cash-form-group">
                  <label>
                    طريقة الدفع
                    <span>*</span>
                  </label>

                 <select
                    name="paymentMethod"
                    value={form.paymentMethod}
                    onChange={handleChange}
                    disabled={saving}
                    className="cash-payment-select"
                  >
                    <option value="">اختر طريقة الدفع</option>
                    <option value="كاش">كاش</option>
                    <option value="انستا باي">انستا باي</option>
                    <option value="فودافون كاش">فودافون كاش</option>
                  </select>
                </div>

              <div className="cash-form-group">
                <label>
                  تاريخ المصروف
                  <span>*</span>
                </label>

                <input
                  type="date"
                  name="expenseDate"
                  value={
                    form.expenseDate
                  }
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>

              <div className="cash-form-group">
                <label>
                  الملاحظات
                </label>

                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="أدخل أي ملاحظات..."
                  rows="4"
                  disabled={saving}
                />
              </div>

              <div className="cash-modal-actions">
                <button
                  type="button"
                  className="cash-cancel-button"
                  onClick={closeModal}
                  disabled={saving}
                >
                  <FiX size={15} />

                  إلغاء
                </button>

                <button
                  type="submit"
                  className="cash-save-button"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <FiRefreshCw
                        size={15}
                        className="cash-spin"
                      />

                      جاري الحفظ...
                    </>
                  ) : (
                    <>
                      <FiSave
                        size={15}
                      />

                      {editingExpense
                        ? "حفظ التعديل"
                        : "حفظ المصروف"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Expenses;
